import { useState } from 'react';
import { Music, Lightbulb, ArrowRight, Info, X } from 'lucide-react';
import Button from '../components/Button';
import Tag from '../components/Tag';
import StoryCardItem from '../components/StoryCardItem';
import { useApp } from '../lib/AppContext';
import { useOnboarding } from '../lib/useOnboarding';
import { INTERESTS } from '../lib/constants';
import { MOCK_CARDS } from '../lib/mockData';
import { StoryCard } from '../lib/types';

export default function Onboarding() {
  const { navigateTo } = useApp();
  const { selectedTopics, setSelectedTopics, markOnboardingComplete } = useOnboarding();
  const [step, setStep] = useState(1);
  const [localSelectedInterests, setLocalSelectedInterests] = useState<string[]>(selectedTopics);
  const [showWhyWeAsk, setShowWhyWeAsk] = useState(false);

  const toggleInterest = (interest: string) => {
    if (localSelectedInterests.includes(interest)) {
      setLocalSelectedInterests(localSelectedInterests.filter(i => i !== interest));
    } else if (localSelectedInterests.length < 6) {
      setLocalSelectedInterests([...localSelectedInterests, interest]);
    }
  };

  const handleComplete = () => {
    console.log('[Onboarding] Button clicked! Topics:', localSelectedInterests);

    // Update topics
    setSelectedTopics(localSelectedInterests);

    // Mark onboarding as complete
    markOnboardingComplete();

    // Navigate to home
    navigateTo('home');

    console.log('[Onboarding] Completed - navigating to Home');
  };

  const handleSkip = () => {
    setSelectedTopics([]);
    markOnboardingComplete();
    navigateTo('home');
  };

  // STEP 1: Intro Screen
  if (step === 1) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-between px-8 py-12">
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="w-32 h-32 mb-8 flex items-center justify-center">
            <Music className="w-24 h-24 text-[#40E0D0]" strokeWidth={1.5} />
          </div>
          <h1 className="text-[28px] font-semibold text-[#F5F5F5] text-center mb-4">
            Music is how you feel the world.
          </h1>
          <p className="text-[16px] text-[#A0A0A0] text-center max-w-md">
            civicbeat turns songs into tiny moments of understanding—about power, justice, and the world around you.
          </p>
        </div>

        <div className="w-full max-w-sm">
          <div className="flex justify-center gap-2 mb-6">
            <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
          </div>
          <Button fullWidth variant="primary" onClick={() => setStep(2)}>
            Let's Go
          </Button>
          <p className="text-xs text-neutral-400 text-center mt-2">
            Swipe to understand the power behind the music.
          </p>
        </div>
      </div>
    );
  }

  // STEP 2: Three Tiny Steps Explainer
  if (step === 2) {
    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-between px-8 py-12">
        <div className="flex-1 flex flex-col justify-center w-full max-w-md">
          <h2 className="text-[24px] font-semibold text-[#F5F5F5] text-center mb-2">
            Three tiny steps.
          </h2>
          <p className="text-[14px] text-[#A0A0A0] text-center mb-12">
            Each story takes less than a minute.
          </p>

          <div className="space-y-6">
            <div className="flex gap-4 bg-[#181818] p-4 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-[#40E0D0] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                <Music className="w-5 h-5 text-[#40E0D0]" />
              </div>
              <div>
                <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-1">
                  LISTEN
                </h3>
                <p className="text-[14px] text-[#A0A0A0]">
                  A song, lyric, or cultural moment
                </p>
              </div>
            </div>

            <div className="flex gap-4 bg-[#181818] p-4 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-[#40E0D0] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-[#40E0D0]" />
              </div>
              <div>
                <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-1">
                  LEARN
                </h3>
                <p className="text-[14px] text-[#A0A0A0]">
                  30–60 seconds of political context
                </p>
              </div>
            </div>

            <div className="flex gap-4 bg-[#181818] p-4 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-[#40E0D0] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-5 h-5 text-[#40E0D0]" />
              </div>
              <div>
                <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-1">
                  ACT
                </h3>
                <p className="text-[14px] text-[#A0A0A0]">
                  A micro-action that feels doable
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-sm">
          <div className="flex justify-center gap-2 mb-6">
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
          </div>
          <Button fullWidth variant="primary" onClick={() => setStep(3)}>
            Continue
          </Button>
          <Button
            variant="tertiary"
            fullWidth
            onClick={handleSkip}
            className="mt-4"
          >
            Skip Intro
          </Button>
        </div>
      </div>
    );
  }

  // STEP 3: Topics Selection ("What moves you?")
  if (step === 3) {
    const canProceed = localSelectedInterests.length >= 2;

    return (
      <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-between px-8 py-12">
        <div className="flex-1 flex flex-col justify-center w-full max-w-md">
          <div className="flex items-center justify-center gap-2 mb-3">
            <h2 className="text-[28px] font-semibold text-[#F5F5F5] text-center">
              What moves you?
            </h2>
            <button
              type="button"
              onClick={() => setShowWhyWeAsk(true)}
              className="text-[#A0A0A0] hover:text-[#40E0D0] transition-colors"
              aria-label="Why we ask"
            >
              <Info className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[16px] text-[#A0A0A0] text-center mb-12">
            Pick the topics that feel closest to you right now. They'll help us bring you songs that reflect your world — and you can update them at any time.
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex justify-center gap-3">
              {INTERESTS.slice(0, 5).map(interest => (
                <Tag
                  key={interest}
                  selected={localSelectedInterests.includes(interest)}
                  onClick={() => toggleInterest(interest)}
                  variant="pill"
                >
                  {interest}
                </Tag>
              ))}
            </div>
            <div className="flex justify-center gap-3">
              {INTERESTS.slice(5, 10).map(interest => (
                <Tag
                  key={interest}
                  selected={localSelectedInterests.includes(interest)}
                  onClick={() => toggleInterest(interest)}
                  variant="pill"
                >
                  {interest}
                </Tag>
              ))}
            </div>
          </div>

          <p className="text-[14px] text-[#A0A0A0] text-center">
            {localSelectedInterests.length} selected (min 2, max 6)
          </p>
        </div>

        {/* "Why we ask" Modal */}
        {showWhyWeAsk && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-6"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
            onClick={() => setShowWhyWeAsk(false)}
          >
            <div
              className="bg-[#1a1a1a] rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-[18px] font-semibold text-[#F5F5F5]">
                  Why we ask
                </h3>
                <button
                  onClick={() => setShowWhyWeAsk(false)}
                  className="text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-[15px] text-[#D0D0D0] leading-relaxed mb-6">
                We use these topics to shape your civicbeat feed so you see stories that match what matters to you. You can always change your interests later in Settings.
              </p>
              <Button
                variant="primary"
                fullWidth
                onClick={() => setShowWhyWeAsk(false)}
              >
                Got it
              </Button>
            </div>
          </div>
        )}

        <div className="w-full max-w-sm">
          <div className="flex justify-center gap-2 mb-6">
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
          </div>
          <Button
            fullWidth
            onClick={() => setStep(4)}
            disabled={!canProceed}
            variant="primary"
          >
            Create My Civicbeat
          </Button>
          <Button
            variant="tertiary"
            fullWidth
            onClick={handleSkip}
            className="mt-4"
          >
            Skip for now
          </Button>
        </div>

      </div>
    );
  }

  // STEP 4: Preview Screen (NEW)
  if (step === 4) {
    const getPreviewCards = (): StoryCard[] => {
      const matchingCards = MOCK_CARDS.filter(card =>
        localSelectedInterests.some(interest =>
          card.tags.includes(interest) ||
          card.primaryIssueTag === interest ||
          card.secondaryIssueTags.includes(interest)
        )
      );

      return matchingCards.slice(0, 3).map(card => ({
        ...card,
        secondaryIssueTags: card.secondaryIssueTags.filter(tag =>
          localSelectedInterests.includes(tag)
        )
      }));
    };

    const previewCards = getPreviewCards();

    return (
      <div className="min-h-screen bg-[#111111] px-6 py-12 overflow-y-auto">
        <div className="max-w-md mx-auto">
          <p className="text-[11px] font-semibold text-[#40E0D0] tracking-wider text-center mb-3">
            FOR YOU · PREVIEW
          </p>

          <h2 className="text-[28px] font-semibold text-[#F5F5F5] text-center mb-3">
            Here's a glimpse of your civicbeat feed
          </h2>

          <p className="text-[15px] text-[#A0A0A0] text-center mb-10 leading-relaxed">
            Based on what moves you, we'll surface stories that connect music to these issues.
          </p>

          <div className="space-y-4 mb-10">
            {previewCards.length > 0 ? (
              previewCards.map(card => (
                <StoryCardItem key={card.id} card={card} />
              ))
            ) : (
              MOCK_CARDS.slice(0, 3).map(card => (
                <StoryCardItem key={card.id} card={card} />
              ))
            )}
          </div>

          <div className="space-y-3 pb-8">
            <div className="flex justify-center gap-2 mb-6">
              <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
              <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
              <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
              <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
              <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
            </div>

            <Button
              fullWidth
              onClick={() => setStep(5)}
              variant="primary"
            >
              Looks Good — Continue
            </Button>

            <Button
              variant="tertiary"
              fullWidth
              onClick={() => setStep(3)}
            >
              Edit Topics
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 5: Final Screen ("Your civicbeat is ready")
  return (
    <div className="min-h-screen bg-[#111111] flex flex-col items-center justify-center px-8 py-12">
      <div className="flex-1 flex flex-col items-center justify-center max-w-md w-full">
        <div className="w-20 h-20 rounded-full bg-[#40E0D0] bg-opacity-20 flex items-center justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-[#40E0D0] flex items-center justify-center">
            <svg
              className="w-6 h-6 text-[#111111]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        <h2 className="text-[24px] font-semibold text-[#F5F5F5] text-center mb-4">
          Your civicbeat is ready.
        </h2>

        {localSelectedInterests.length > 0 ? (
          <div className="mb-8 w-full">
            <p className="text-[14px] text-[#A0A0A0] text-center mb-4">
              We'll start you with stories about:
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {localSelectedInterests.map(interest => (
                <Tag key={interest} selected variant="pill">
                  {interest}
                </Tag>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-[14px] text-[#A0A0A0] text-center mb-8">
            We'll show you a mix of stories to help you discover what moves you. Every card is a tiny step toward understanding power.
          </p>
        )}
      </div>

      <div className="w-full max-w-sm">
        <div className="flex justify-center gap-2 mb-6">
          <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
          <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
          <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
          <div className="w-2 h-2 rounded-full border border-[#40E0D0]"></div>
          <div className="w-2 h-2 rounded-full bg-[#40E0D0]"></div>
        </div>
        <Button
          variant="primary"
          fullWidth
          onClick={(e) => {
            console.log('[Button] Click event fired!', e);
            handleComplete();
          }}
        >
          Explore Your Feed
        </Button>
      </div>
    </div>
  );
}
